import { PrivacyPage, TermsPage } from '@dealstack/frontend/shared/pages/Legal/LegalPage';
import { Auth } from '@dealstack/frontend/app/pages/Auth/Auth';
import { ProtectedRoute } from '@dealstack/frontend/shared/pages/ProtectedRoute/ProtectedRoute';
import { Navigate, Route } from 'react-router-dom';
import { useAppDispatch } from '@dealstack/frontend/app/hooks/redux';
import { ApplicationType } from '@dealstack/shared/lib/generated';
import { DealDashboard as DealRoot } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealDashboard';
import { CreateDeal as DealCreate } from '@dealstack/frontend/app/pages/Project/CreateDeal/CreateDeal';
import { Dashboard } from '@dealstack/frontend/app/pages/Dashboard/Dashboard';
import { ManageDocumentTemplates } from '@dealstack/frontend/app/pages/DocumentTemplates/ManageDocumentTemplates';
import { Page404 } from '@dealstack/frontend/shared/pages/Page404/Page404';
import { Export as ExportPage } from '@dealstack/frontend/app/pages/Export/Export';
import { Profile as UserProfile } from '@dealstack/frontend/shared/components/Profile/Profile';
import { GenericError as GenericErrorPage } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { EidEasyFtnCallback } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasyFtnCallback';
import { EidEasyOtpCallback } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasyOtpCallback';
import { Organization } from '@dealstack/frontend/app/pages/Organization/Organization';
import { Organizations } from '@dealstack/frontend/app/pages/Organizations/Organizations';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import {
	DEFAULT_GUEST_ROUTE,
	DEFAULT_PROTECTED_ROUTE,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes';
import { setApplicationType } from '@dealstack/frontend/shared/mechanism/redux/reducers/global/global';
import { HelpBlogWithDeals } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlog.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { useIsDealstackSupport } from '@dealstack/frontend/shared/hooks/useIsDealstackSupport';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { useEffect, type ReactNode } from 'react';

interface Props {
	className?: string;
}

export const Routing = ({ className }: Props) => {
	const dispatch = useAppDispatch();
	const applicationType = ApplicationType.DEALSTACK;
	useEffect(() => {
		dispatch(setApplicationType(applicationType));
	}, []);

	const helpBlogRoute = (
		<ProtectedRoute
			defaultGuestRoute={DEFAULT_GUEST_ROUTE}
			dispatch={dispatch}
			applicationType={applicationType}
			defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
			hideOverflowX={false}
		>
			<HelpBlogWithDeals dataset={ApplicationType.DEALSTACK} />
		</ProtectedRoute>
	);

	// Templates are global and DealStack-authored, so the API is @SuperAdminRequired() — the
	// route must gate on support status as well as the flag, or a flagged-on tenant admin
	// reaches the page and every call 403s. Renders nothing while ConfigCat resolves, rather
	// than flashing a redirect at someone who does have it.
	const { value: documentManagedTemplatesEnabled, loading: documentManagedTemplatesLoading } =
		useDealstackFeatureFlag(FEATURE_FLAG.DOCUMENT_MANAGED_TEMPLATES);
	const isDealstackSupport = useIsDealstackSupport();
	let documentTemplatesElement: ReactNode;
	if (documentManagedTemplatesLoading) {
		documentTemplatesElement = null;
	} else if (documentManagedTemplatesEnabled && isDealstackSupport) {
		documentTemplatesElement = <ManageDocumentTemplates />;
	} else {
		documentTemplatesElement = <Navigate to={DEFAULT_PROTECTED_ROUTE} replace />;
	}

	return (
		<div className={className}>
			<SentryRoutes>
				<Route
					path={`${ROUTES.AUTH}/*`}
					element={<Auth applicationType={applicationType} />}
				/>
				<Route
					path={ROUTES.DASHBOARD}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<Dashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTES.DEAL}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<DealRoot />
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTES.DEAL_CREATE}`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<DealCreate />
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTES.TEMPLATES}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							{documentTemplatesElement}
						</ProtectedRoute>
					}
				/>
				<Route
					path={ROUTES.EXPORT}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<ExportPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path={ROUTES.ORGANIZATIONS}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<Organizations />
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTES.ORGANIZATION_BY_ID}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<Organization />
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTES.PROFILE}/*`}
					element={
						<ProtectedRoute
							defaultGuestRoute={DEFAULT_GUEST_ROUTE}
							dispatch={dispatch}
							applicationType={applicationType}
							defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}
						>
							<UserProfile
								dashboardRoute={DEFAULT_PROTECTED_ROUTE}
								userProfileRoute={ROUTES.PROFILE}
							/>
						</ProtectedRoute>
					}
				/>
				<Route path={`${ROUTES.HELP_BLOG}/*`} element={helpBlogRoute} />
				<Route path={`${ROUTES.DEAL}${ROUTES.HELP_BLOG}/*`} element={helpBlogRoute} />
				<Route path={ROUTES.TERMS} element={<TermsPage />} />
				<Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
				<Route path={ROUTES.EID_EASY_FTN_CALLBACK} element={<EidEasyFtnCallback />} />
				<Route path={ROUTES.EID_EASY_OTP_CALLBACK} element={<EidEasyOtpCallback />} />
				<Route path={ROUTES.PAGE_NOT_FOUND} element={<Page404 />} />
				<Route path={ROUTES.GENERIC_ERROR} element={<GenericErrorPage />} />
				<Route path="*" element={<Navigate to={ROUTES.DASHBOARD} />} />
			</SentryRoutes>
		</div>
	);
};
